import { useState, useEffect } from "react";
import axios from "axios";
import { parseTableWithNormalizedHeaders, formatTableResultAsJSON, titleToTableName } from "../utils/manualTableParser";
import { getApiBase } from "../utils/config";
import "./TableParser.css";

const API_BASE = getApiBase();

// Map table title (e.g. "HISTORY OF PAID FEES") to backend payload key for save (like AI version)
const TABLE_TITLE_TO_SAVE_KEY = {
  "HISTORY OF PAID FEES": "historyOfPaidFees",
  "HISTORY OF CHALLAN": "historyOfChallan",
  "HISTORY OF HYPOTHECATION": "historyOfHypothecation",
  "HISTORY OF INSURANCE": "historyOfInsurance",
  "HISTORY OF ROAD TAX": "historyOfRoadTax",
  "HISTORY OF PAID ROAD TAX": "historyOfRoadTax",
  "HISTORY OF OWNERSHIP": "historyOfOwnership",
  "HISTORY OF TRANSFER OF OWNERSHIP": "historyOfOwnership",
  "HISTORY OF TRANSFER OWNERSHIP": "historyOfOwnership",
  "HISTORY OF BLACKLIST": "historyOfBlacklist",
  "HISTORY OF PERMIT": "historyOfPermit",
  "HISTORY OF TAX CLEAR": "historyOfTaxClear",
  "HISTORY OF FITNESS": "historyOfFitness",
  "HISTORY OF CHANGED VEHICLE RECORD BY ADMIN": "historyOfChangedVehicle",
  "HISTORY OF RC PRINT DETAILS": "historyOfRcPrint",
  "HISTORY OF NOC": "historyOfNoc",
};

// Save key → database table name (for display and saving as per table name)
const SAVE_KEY_TO_DB_TABLE = {
  historyOfPaidFees: "history_of_paid_fees",
  historyOfChallan: "history_of_challans",
  historyOfHypothecation: "history_of_hypothecations",
  historyOfInsurance: "history_of_insurances",
  historyOfRoadTax: "history_of_road_taxes",
  historyOfOwnership: "history_of_transfer_ownerships",
  historyOfBlacklist: "history_of_blacklists",
  historyOfPermit: "history_of_permits",
  historyOfTaxClear: "history_of_tax_clears",
  historyOfFitness: "history_of_fitness",
  historyOfChangedVehicle: "history_of_changed_vehicle_record_by_admins",
  historyOfRcPrint: "history_of_rc_print_details",
  historyOfNoc: "history_of_noc",
};

// Options for "Save to table" dropdown: label and save key (data is saved to the corresponding DB table)
const SAVE_TABLE_OPTIONS = [
  { value: "historyOfChallan", label: "History of Challan", tableName: "history_of_challans" },
  { value: "historyOfPaidFees", label: "History of Paid Fees", tableName: "history_of_paid_fees" },
  { value: "historyOfRoadTax", label: "History of Road Tax", tableName: "history_of_road_taxes" },
  { value: "historyOfInsurance", label: "History of Insurance", tableName: "history_of_insurances" },
  { value: "historyOfHypothecation", label: "History of Hypothecation", tableName: "history_of_hypothecations" },
  { value: "historyOfOwnership", label: "History of Ownership", tableName: "history_of_transfer_ownerships" },
  { value: "historyOfBlacklist", label: "History of Blacklist", tableName: "history_of_blacklists" },
  { value: "historyOfPermit", label: "History of Permit", tableName: "history_of_permits" },
  { value: "historyOfTaxClear", label: "History of Tax Clear", tableName: "history_of_tax_clears" },
  { value: "historyOfFitness", label: "History of Fitness", tableName: "history_of_fitness" },
  { value: "historyOfChangedVehicle", label: "History of Changed Vehicle", tableName: "history_of_changed_vehicle_record_by_admins" },
  { value: "historyOfRcPrint", label: "History of RC Print", tableName: "history_of_rc_print_details" },
  { value: "historyOfNoc", label: "History of NOC", tableName: "history_of_noc" },
];

// Helper: get first defined value from row for any of the given keys (parsed REGISTER_NO + backend Receipt_No etc.)
const getVal = (row, ...keys) => {
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return null;
};

// Helper: get value or empty string (for save - leave empty columns empty in that row)
const getValOrEmpty = (row, ...keys) => {
  const v = getVal(row, ...keys);
  return v !== null && v !== undefined ? String(v).trim() : "";
};

// Normalize a header/key to compare with backend column name (e.g. "Receipt No" -> "receipt_no", "RECEIPT_NO" -> "receipt_no")
const normalizeKeyForMatch = (k) =>
  String(k || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");

// Aliases: normalized row key -> backend key (so e.g. "application_no" from pasted "Application No" maps to "Appl_No")
const ROW_KEY_TO_BACKEND_ALIASES = {
  historyOfFitness: {
    application_no: "Appl_No",
    appl_no: "Appl_No",
    fitness_check_date: "Fitness_Check_Date",
    fitness_upto: "Fitness_UPTO",
    operation_date: "Operation_Date",
    fitness_officer_name1: "Fitness_Officer_Name1",
    fitness_officer_name2: "Fitness_Officer_Name2",
  },
};

// Merge any value from the raw row into the mapped row so we never drop details (e.g. extra aliases or user edits)
function mergeRowIntoMapped(row, mapped, backendKeys, sectionKey) {
  if (!row || typeof row !== "object" || !mapped || !Array.isArray(backendKeys)) return;
  const normalizedBackend = new Map(backendKeys.map((bk) => [normalizeKeyForMatch(bk), bk]));
  const aliases = ROW_KEY_TO_BACKEND_ALIASES[sectionKey] || {};
  for (const [rowKey, value] of Object.entries(row)) {
    const n = normalizeKeyForMatch(rowKey);
    const backendKey = normalizedBackend.get(n) || aliases[n];
    if (backendKey != null && (mapped[backendKey] === undefined || mapped[backendKey] === null || mapped[backendKey] === "")) {
      const v = value !== undefined && value !== null ? String(value).trim() : "";
      mapped[backendKey] = v || null;
    }
  }
}

// Static structure for each table as per database schema (exact column order from DB)
// Used as single source of truth for mapping and payload; system columns excluded from payload.
const SYSTEM_COLUMNS = ["id", "Registration_No", "created_at", "updated_at"];

const DB_TABLE_SCHEMA = {
  historyOfChallan: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Serial_No", label: "SERIAL NO" },
    { key: "Challan_No", label: "CHALLAN NO" },
    { key: "Offense_Details", label: "OFFENSE DETAILS" },
    { key: "Challan_Place", label: "CHALLAN PLACE" },
    { key: "Challan_Date_Time", label: "CHALLAN DATE TIME" },
    { key: "State", label: "STATE" },
    { key: "RTO", label: "RTO" },
    { key: "Accused_Name", label: "ACCUSED NAME" },
    { key: "Amount", label: "AMOUNT" },
    { key: "Challan_Status", label: "CHALLAN STATUS" },
    { key: "Payment_Source", label: "PAYMENT SOURCE" },
    { key: "Payment_Date", label: "PAYMENT DATE" },
    { key: "Receipt_Number", label: "RECEIPT NUMBER" },
    { key: "sent_to_reg_court", label: "SENT TO REG COURT" },
    { key: "remark", label: "REMARK" },
    { key: "dl_no", label: "DL NO" },
    { key: "driver_name", label: "DRIVER NAME" },
    { key: "owner_name", label: "OWNER NAME" },
    { key: "department", label: "DEPARTMENT" },
    { key: "document_impounded", label: "DOCUMENT IMPOUNDED" },
    { key: "amount_of_fine_imposed", label: "AMOUNT OF FINE IMPOSED" },
    { key: "court_address", label: "COURT ADDRESS" },
    { key: "court_name", label: "COURT NAME" },
    { key: "date_of_proceeding", label: "DATE OF PROCEEDING" },
    { key: "sent_to_court_on", label: "SENT TO COURT ON" },
    { key: "sent_to_virtual_court", label: "SENT TO VIRTUAL COURT" },
    { key: "rto_distric_name", label: "RTO DISTRICT NAME" },
    { key: "created_at", label: "CREATED AT" },
    { key: "updated_at", label: "UPDATED AT" },
  ],
  historyOfPaidFees: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Receipt_No", label: "RECEIPT NO" },
    { key: "Receipt_Date", label: "RECEIPT DATE" },
    { key: "Fee_Amount", label: "FEE AMOUNT" },
    { key: "Fine", label: "FINE" },
    { key: "Fee_Particular", label: "FEE PARTICULAR" },
    { key: "Office", label: "OFFICE" },
    { key: "GRN_No", label: "GRN NO" },
    { key: "Fee_Collected_As", label: "FEE COLLECTED AS" },
    { key: "collection_mode", label: "COLLECTION MODE" },
    { key: "created_at", label: "CREATED AT" },
    { key: "updated_at", label: "UPDATED AT" },
  ],
  historyOfRoadTax: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Receipt_No", label: "RECEIPT NO" },
    { key: "Tax_From", label: "TAX FROM" },
    { key: "Tax_Upto", label: "TAX UPTO" },
    { key: "Tax_Type", label: "TAX TYPE" },
    { key: "Challan_Date", label: "CHALLAN DATE" },
    { key: "Total_Amount", label: "TOTAL AMOUNT" },
    { key: "Office", label: "OFFICE" },
    { key: "Tax", label: "TAX" },
    { key: "Penalty", label: "PENALTY" },
    { key: "GRN_No", label: "GRN NO" },
    { key: "Breakup", label: "BREAKUP" },
    { key: "Tax_Mode", label: "TAX MODE" },
    { key: "collection_mode", label: "COLLECTION MODE" },
    { key: "created_at", label: "CREATED AT" },
    { key: "updated_at", label: "UPDATED AT" },
  ],
  historyOfInsurance: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Sr_No", label: "SR NO" },
    { key: "Insurance_Company", label: "INSURANCE COMPANY" },
    { key: "Insurance_Type", label: "INSURANCE TYPE" },
    { key: "Insurance_From", label: "INSURANCE FROM" },
    { key: "Insurance_Upto", label: "INSURANCE UPTO" },
    { key: "Cover_Note_No", label: "COVER NOTE NO" },
    { key: "Office", label: "OFFICE" },
    { key: "created_at", label: "CREATED AT" },
    { key: "updated_at", label: "UPDATED AT" },
  ],
  historyOfHypothecation: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Application_No", label: "APPLICATION NO" },
    { key: "Hypothecation_Type", label: "HYPOTHECATION TYPE" },
    { key: "Financer_Name", label: "FINANCER NAME" },
    { key: "Financer_Address", label: "FINANCER ADDRESS" },
    { key: "From_Date", label: "FROM DATE" },
    { key: "To_Date", label: "TO DATE" },
    { key: "Termination_Date", label: "TERMINATION DATE" },
    { key: "Office", label: "OFFICE" },
    { key: "created_at", label: "CREATED AT" },
    { key: "updated_at", label: "UPDATED AT" },
  ],
  historyOfTaxClear: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Sr_No", label: "SR NO" },
    { key: "Tax_Description", label: "TAX DESCRIPTION" },
    { key: "Tax_Clear_To", label: "TAX CLEAR TO" },
    { key: "TCR_No", label: "TCR NO" },
    { key: "Operation_Date", label: "OPERATION DATE" },
    { key: "Remarks", label: "REMARKS" },
    { key: "Office", label: "OFFICE" },
    { key: "created_at", label: "CREATED AT" },
    { key: "updated_at", label: "UPDATED AT" },
  ],
  historyOfPermit: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Application_No", label: "APPLICATION NO" },
    { key: "Permit_No", label: "PERMIT NO" },
    { key: "Issue_Date", label: "ISSUE DATE" },
    { key: "Valid_From", label: "VALID FROM" },
    { key: "Valid_Upto", label: "VALID UPTO" },
    { key: "Permit_Type", label: "PERMIT TYPE" },
    { key: "Permit_Category", label: "PERMIT CATEGORY" },
    { key: "Office", label: "OFFICE" },
    { key: "created_at", label: "CREATED AT" },
    { key: "updated_at", label: "UPDATED AT" },
  ],
  historyOfBlacklist: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Complain_File_Number", label: "COMPLAIN FILE NUMBER" },
    { key: "Complain_Date", label: "COMPLAIN DATE" },
    { key: "Complain_Entered_By", label: "COMPLAIN ENTERED BY" },
    { key: "Action_Taken", label: "ACTION TAKEN" },
    { key: "Action_Entered_By", label: "ACTION ENTERED BY" },
    { key: "Action_Date", label: "ACTION DATE" },
    { key: "Office", label: "OFFICE" },
    { key: "Compounding_Amount", label: "COMPOUNDING AMOUNT" },
    { key: "created_at", label: "CREATED AT" },
    { key: "updated_at", label: "UPDATED AT" },
  ],
  historyOfFitness: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Appl_No", label: "APPL NO" },
    { key: "Fitness_Check_Date", label: "FITNESS CHECK DATE" },
    { key: "Result", label: "RESULT" },
    { key: "Fitness_UPTO", label: "FITNESS UPTO" },
    { key: "NID", label: "NID" },
    { key: "Operation_Date", label: "OPERATION DATE" },
    { key: "Fitness_Officer_Name1", label: "FITNESS OFFICER NAME1" },
    { key: "Fitness_Officer_Name2", label: "FITNESS OFFICER NAME2" },
    { key: "Office", label: "OFFICE" },
    { key: "Remark", label: "REMARK" },
    { key: "created_at", label: "CREATED AT" },
  ],
  historyOfOwnership: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Application_No", label: "APPLICATION NO" },
    { key: "Ownership_Serial", label: "OWNERSHIP SERIAL" },
    { key: "Owner_From", label: "OWNER FROM" },
    { key: "Owner_Upto", label: "OWNER UPTO" },
    { key: "Owner_Name", label: "OWNER NAME" },
    { key: "Father_Husband_Name", label: "FATHER HUSBAND NAME" },
    { key: "Present_Address", label: "PRESENT ADDRESS" },
    { key: "Permanent_Address", label: "PERMANENT ADDRESS" },
    { key: "Owner_Type", label: "OWNER TYPE" },
    { key: "Sale_Auction_Date", label: "SALE AUCTION DATE" },
    { key: "Sale_Amount", label: "SALE AMOUNT" },
    { key: "Reason", label: "REASON" },
    { key: "Office", label: "OFFICE" },
    { key: "Deemed_Transfer_Time_Days", label: "DEEMED TRANSFER TIME DAYS" },
    { key: "created_at", label: "CREATED AT" },
    { key: "updated_at", label: "UPDATED AT" },
  ],
  historyOfChangedVehicle: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Sr_No", label: "SR NO" },
    { key: "Changed_By", label: "CHANGED BY" },
    { key: "Changed_Data", label: "CHANGED DATA" },
    { key: "Changed_On", label: "CHANGED ON" },
    { key: "Office", label: "OFFICE" },
    { key: "created_at", label: "CREATED AT" },
    { key: "updated_at", label: "UPDATED AT" },
  ],
  historyOfRcPrint: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Application_No", label: "APPLICATION NO" },
    { key: "Purpose", label: "PURPOSE" },
    { key: "Printed_On", label: "PRINTED ON" },
    { key: "Printed_By", label: "PRINTED BY" },
    { key: "created_at", label: "CREATED AT" },
    { key: "updated_at", label: "UPDATED AT" },
  ],
  historyOfNoc: [
    { key: "id", label: "ID" },
    { key: "Registration_No", label: "REGISTRATION NO" },
    { key: "Application_No", label: "APPLICATION NO" },
    { key: "NOC_No", label: "NOC NO" },
    { key: "NOC_Date", label: "NOC DATE" },
    { key: "State_To", label: "STATE TO" },
    { key: "Office_To", label: "OFFICE TO" },
    { key: "Dispatch_No", label: "DISPATCH NO" },
    { key: "New_Owner", label: "NEW OWNER" },
    { key: "Office", label: "OFFICE" },
    { key: "created_at", label: "CREATED AT" },
    { key: "updated_at", label: "UPDATED AT" },
  ],
};

// Infer saveKey from detected title keywords (handles slight title variations)
function inferSaveKeyFromDetectedTitle(tableTitle) {
  if (!tableTitle) return "";
  const t = String(tableTitle).toUpperCase();

  if (t.includes("CHALLAN")) return "historyOfChallan";
  if (t.includes("HYPOTHECATION")) return "historyOfHypothecation";
  if (t.includes("INSURANCE")) return "historyOfInsurance";
  if (t.includes("ROAD TAX")) return "historyOfRoadTax";
  if (t.includes("PAID FEES") || (t.includes("PAID") && t.includes("FEES"))) return "historyOfPaidFees";

  if (t.includes("OWNERSHIP")) return "historyOfOwnership";

  if (t.includes("BLACKLIST")) return "historyOfBlacklist";
  if (t.includes("PERMIT")) return "historyOfPermit";
  if (t.includes("TAX CLEAR")) return "historyOfTaxClear";
  if (t.includes("FITNESS")) return "historyOfFitness";

  if (t.includes("CHANGED VEHICLE")) return "historyOfChangedVehicle";
  if (t.includes("RC PRINT")) return "historyOfRcPrint";
  if (t.includes("NOC")) return "historyOfNoc";

  return "";
}

// Derived: payload column names per section (schema minus system columns)
const BACKEND_KEYS_BY_SECTION = Object.fromEntries(
  Object.entries(DB_TABLE_SCHEMA).map(([saveKey, cols]) => [
    saveKey,
    cols.filter((c) => !SYSTEM_COLUMNS.includes(c.key)).map((c) => c.key),
  ])
);

// Map parsed table rows to backend column names; ensure every backend column is present and merge in any value from raw row so no details are lost
const mapParsedRowsToBackend = (saveKey, rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  let mapped;
  switch (saveKey) {
    case "historyOfPaidFees":
      mapped = rows.map((row) => ({
        Receipt_No: getVal(row, "RECEIPT_NO", "Receipt_No", "ReceiptNo", "receipt_no"),
        Receipt_Date: getValOrEmpty(row, "RECEIPT_DATE", "Receipt_Date", "ReceiptDate", "receipt_date", "RECEIPT DT", "Receipt Dt", "DATE", "Date"),
        Fee_Amount: getVal(row, "FEE_AMOUNT", "Fee_Amount", "FeeAmount", "fee_amount"),
        Fine: getVal(row, "FINE", "Fine", "fine"),
        Fee_Particular: getVal(row, "FEE_PARTICULAR", "Fee_Particular", "FeeParticular", "fee_particular"),
        Office: getVal(row, "OFFICE", "Office", "office"),
        GRN_No: getVal(row, "GRN_NO", "GRN_No", "GRNNo", "grn_no"),
        Fee_Collected_As: getVal(row, "FEE_COLLECTED_AS", "Fee_Collected_As", "FeeCollectedAs", "fee_collected_as"),
        collection_mode: getVal(row, "collection_mode", "COLLECTION_MODE", "Collection_Mode", "collectionMode"),
      }));
      break;
    case "historyOfRoadTax":
      mapped = rows.map((row) => ({
        Receipt_No: getVal(row, "RECEIPT_NO", "Receipt_No", "ReceiptNo", "receipt_no"),
        Tax_From: getVal(row, "TAX_FROM", "Tax_From", "TaxFrom", "tax_from"),
        Tax_Upto: getVal(row, "TAX_UPTO", "Tax_Upto", "TaxUpto", "tax_upto"),
        Tax_Type: getVal(row, "TAX_TYPE", "Tax_Type", "TaxType", "tax_type"),
        Challan_Date: getValOrEmpty(row, "CHALLAN_DATE", "Challan_Date", "ChallanDate", "challan_date", "CHALLAN DT", "Challan Dt", "DATE", "Date"),
        Total_Amount: getVal(row, "TOTAL_AMOUNT", "Total_Amount", "TotalAmount", "total_amount"),
        Office: getVal(row, "OFFICE", "Office", "office"),
        Tax: getVal(row, "TAX", "Tax", "tax"),
        Penalty: getVal(row, "PENALTY", "Penalty", "penalty"),
        GRN_No: getVal(row, "GRN_NO", "GRN_No", "GRNNo", "grn_no"),
        Breakup: getVal(row, "BREAKUP", "Breakup", "breakup"),
        Tax_Mode: getVal(row, "TAX_MODE", "Tax_Mode", "TaxMode", "tax_mode"),
        collection_mode: getVal(row, "COLLECTION_MODE", "collection_mode", "Collection_Mode", "collectionMode"),
      }));
      break;
    case "historyOfInsurance":
      mapped = rows.map((row) => ({
        Sr_No: getVal(row, "SR_NO", "Sr_No", "SrNo", "sr_no"),
        Insurance_Company: getVal(row, "INSURANCE_COMPANY", "Insurance_Company", "InsuranceCompany", "insurance_company"),
        Insurance_Type: getVal(row, "INSURANCE_TYPE", "Insurance_Type", "InsuranceType", "insurance_type"),
        Insurance_From: getVal(row, "INSURANCE_FROM", "Insurance_From", "InsuranceFrom", "insurance_from"),
        Insurance_Upto: getVal(row, "INSURANCE_UPTO", "Insurance_Upto", "InsuranceUpto", "insurance_upto"),
        Cover_Note_No: getVal(row, "COVER_NOTE_NO", "Cover_Note_No", "CoverNoteNo", "cover_note_no"),
        Office: getVal(row, "OFFICE", "Office", "office"),
      }));
      break;
    case "historyOfHypothecation":
      mapped = rows.map((row) => ({
        Application_No: getVal(row, "APPLICATION_NO", "Application_No", "ApplicationNo", "application_no"),
        Hypothecation_Type: getVal(row, "HYPOTHECATION_TYPE", "Hypothecation_Type", "HypothecationType", "hypothecation_type"),
        Financer_Name: getVal(row, "FINANCER_NAME", "Financer_Name", "FinancerName", "financer_name"),
        Financer_Address: getVal(row, "FINANCER_ADDRESS", "Financer_Address", "FinancerAddress", "financer_address"),
        From_Date: getVal(row, "FROM_DATE", "From_Date", "FromDate", "from_date"),
        To_Date: getVal(row, "TO_DATE", "To_Date", "ToDate", "to_date"),
        Termination_Date: getVal(row, "TERMINATION_DATE", "Termination_Date", "TerminationDate", "termination_date"),
        Office: getVal(row, "OFFICE", "Office", "office"),
      }));
      break;
    case "historyOfTaxClear":
      mapped = rows.map((row) => ({
        Sr_No: getVal(row, "SR_NO", "Sr_No", "SrNo", "sr_no"),
        Tax_Description: getVal(row, "TAX_DESCRIPTION", "Tax_Description", "TaxDescription", "tax_description"),
        Tax_Clear_To: getVal(row, "TAX_CLEAR_TO", "Tax_Clear_To", "TaxClearTo", "tax_clear_to"),
        TCR_No: getVal(row, "TCR_NO", "TCR_No", "TCRNo", "tcr_no"),
        Operation_Date: getVal(row, "OPERATION_DATE", "Operation_Date", "OperationDate", "operation_date"),
        Remarks: getVal(row, "REMARKS", "Remarks", "remarks"),
        Office: getVal(row, "OFFICE", "Office", "office"),
      }));
      break;
    case "historyOfChallan":
      mapped = rows.map((row) => ({
        Serial_No: getValOrEmpty(row, "SERIAL_NO", "Serial_No", "SerialNo", "serial_no"),
        Challan_No: getValOrEmpty(row, "CHALLAN_NO", "Challan_No", "ChallanNo", "challan_no"),
        Offense_Details: getValOrEmpty(row, "OFFENSE_DETAILS_NAME_ACT", "Offense_Details_Name_Act", "Offense_Details", "OffenseDetails", "offense_details"),
        Challan_Place: getValOrEmpty(row, "CHALLAN_PLACE", "Challan_Place", "ChallanPlace", "challan_place"),
        Challan_Date_Time: getValOrEmpty(row, "CHALLAN_DATE_TIME", "Challan_Date_Time", "ChallanDateTime", "challan_date_time"),
        State: getValOrEmpty(row, "STATE", "State", "state"),
        RTO: getValOrEmpty(row, "RTO", "rto"),
        Accused_Name: getValOrEmpty(row, "ACCUSSED_NAME", "Accussed_Name", "Accused_Name", "AccusedName", "accused_name", "accussed_name"),
        Amount: getValOrEmpty(row, "AMOUNT", "Amount", "amount"),
        Challan_Status: getValOrEmpty(row, "CHALLAN_STATUS", "Challan_Status", "ChallanStatus", "challan_status"),
        Payment_Source: getValOrEmpty(row, "PAYMENT_SOURCE", "Payment_Source", "Payment_source", "PaymentSource", "payment_source"),
        Payment_Date: getValOrEmpty(row, "PAYMENT_DATE", "Payment_Date", "PaymentDate", "payment_date"),
        Receipt_Number: getValOrEmpty(row, "RECEIPT_NUMBER", "Receipt_Number", "ReceiptNumber", "receipt_number"),
        sent_to_reg_court: getVal(row, "sent_to_reg_court", "SENT_TO_REG_COURT", "Sent_To_Reg_Court") ?? null,
        remark: getVal(row, "remark", "REMARK", "Remark") ?? null,
        dl_no: getVal(row, "dl_no", "DL_NO", "Dl_No") ?? null,
        driver_name: getVal(row, "driver_name", "DRIVER_NAME", "Driver_Name") ?? null,
        owner_name: getVal(row, "owner_name", "OWNER_NAME", "Owner_Name") ?? null,
        department: getVal(row, "department", "DEPARTMENT", "Department") ?? null,
        document_impounded: getVal(row, "document_impounded", "DOCUMENT_IMPOUNDED", "Document_Impounded") ?? null,
        amount_of_fine_imposed: getVal(row, "amount_of_fine_imposed", "AMOUNT_OF_FINE_IMPOSED", "Amount_Of_Fine_Imposed") ?? null,
        court_address: getVal(row, "court_address", "COURT_ADDRESS", "Court_Address") ?? null,
        court_name: getVal(row, "court_name", "COURT_NAME", "Court_Name") ?? null,
        date_of_proceeding: getVal(row, "date_of_proceeding", "DATE_OF_PROCEEDING", "Date_Of_Proceeding") ?? null,
        sent_to_court_on: getVal(row, "sent_to_court_on", "SENT_TO_COURT_ON", "Sent_To_Court_On") ?? null,
        sent_to_virtual_court: getVal(row, "sent_to_virtual_court", "SENT_TO_VIRTUAL_COURT", "Sent_To_Virtual_Court") ?? null,
        rto_distric_name: getVal(row, "rto_distric_name", "RTO_DISTRIC_NAME", "Rto_Distric_Name") ?? null,
      }));
      break;
    case "historyOfPermit":
      mapped = rows.map((row) => ({
        Application_No: getValOrEmpty(row, "APPLICATION_NO", "Application_No", "ApplicationNo", "application_no"),
        Permit_No: getValOrEmpty(row, "PERMIT_NO", "Permit_No", "PermitNo", "permit_no"),
        Issue_Date: getValOrEmpty(row, "ISSUE_DATE", "Issue_Date", "IssueDate", "issue_date"),
        Valid_From: getValOrEmpty(row, "VALID_FROM", "Valid_From", "ValidFrom", "valid_from"),
        Valid_Upto: getValOrEmpty(row, "VALID_UPTO", "Valid_Upto", "ValidUpto", "valid_upto"),
        Permit_Type: getValOrEmpty(row, "PERMIT_TYPE", "Permit_Type", "PermitType", "permit_type"),
        Permit_Category: getValOrEmpty(row, "PERMIT_CATEGORY", "Permit_Category", "PermitCategory", "permit_category"),
        Office: getValOrEmpty(row, "OFFICE", "Office", "office"),
      }));
      break;
    case "historyOfBlacklist":
      mapped = rows.map((row) => ({
        Complain_File_Number: getVal(row, "COMPLAIN_FILE_NUMBER", "Complain_File_Number", "ComplainFileNumber", "complain_file_number"),
        Complain_Date: getVal(row, "COMPLAIN_DATE", "Complain_Date", "ComplainDate", "complain_date"),
        Complain_Entered_By: getVal(row, "COMPLAIN_ENTERED_BY", "Complain_Entered_By", "ComplainEnteredBy", "complain_entered_by"),
        Action_Taken: getVal(row, "ACTION_TAKEN", "Action_Taken", "ActionTaken", "action_taken"),
        Action_Entered_By: getVal(row, "ACTION_ENTERED_BY", "Action_Entered_By", "ActionEnteredBy", "action_entered_by"),
        Action_Date: getVal(row, "ACTION_DATE", "Action_Date", "ActionDate", "action_date"),
        Office: getVal(row, "OFFICE", "Office", "office"),
        Compounding_Amount: getVal(row, "COMPOUNDING_AMOUNT", "Compounding_Amount", "CompoundingAmount", "compounding_amount"),
      }));
      break;
    case "historyOfFitness":
      mapped = rows.map((row, idx) => {
        const applNo = getValOrEmpty(row, "APPL_NO", "Appl_No", "ApplNo", "appl_no", "APPLICATION_NO", "Application_No", "ApplicationNo", "application_no");
        const fitnessCheckDate = getValOrEmpty(row, "FITNESS_CHECK_DATE", "Fitness_Check_Date", "FitnessCheckDate", "fitness_check_date", "Fitness_Check_date");
        const fitnessUpto = getValOrEmpty(row, "FITNESS_UPTO", "Fitness_UPTO", "FitnessUpto", "Fitness_Upto", "fitness_upto");
        let operationDateVal = getValOrEmpty(row, "OPERATION_DATE", "Operation_Date", "OperationDate", "operation_date");
        let nidVal = getVal(row, "NID", "nid");
        const looksLikeDate = (v) => v && /^\d{1,2}-[A-Za-z]{3}-\d{4}/.test(String(v).trim());
        if (looksLikeDate(nidVal) && looksLikeDate(operationDateVal)) {
          operationDateVal = nidVal;
          nidVal = null;
        }
        if (nidVal === null || String(nidVal).trim() === "") nidVal = "0";
        return {
          Appl_No: applNo,
          // Keep exact date string as shown in the manual parser UI.
          Fitness_Check_Date: fitnessCheckDate ? String(fitnessCheckDate).trim() : "",
          Result: getValOrEmpty(row, "RESULT", "Result", "result"),
          Fitness_UPTO: fitnessUpto ? String(fitnessUpto).trim() : "",
          NID: String(nidVal),
          Operation_Date: operationDateVal ? String(operationDateVal).trim() : "",
          Fitness_Officer_Name1: getValOrEmpty(row, "FITNESS_OFFICER_NAME1", "Fitness_Officer_Name1", "fitness_officer_name1", "Fitness_Officer_Name_1", "Fitness Officer Name1"),
          Fitness_Officer_Name2: getValOrEmpty(row, "FITNESS_OFFICER_NAME2", "Fitness_Officer_Name2", "fitness_officer_name2", "Fitness_Officer_Name_2", "Fitness Officer Name2"),
          Office: getValOrEmpty(row, "OFFICE", "Office", "office"),
          Remark: getValOrEmpty(row, "REMARK", "Remark", "remark"),
        };
      });
      break;
    case "historyOfOwnership":
      mapped = rows.map((row) => ({
        Application_No: getVal(row, "APPLICATION_NO", "Application_No", "ApplicationNo", "application_no"),
        Ownership_Serial: getVal(row, "OWNERSHIP_SERIAL", "Ownership_Serial", "OwnershipSerial", "ownership_serial"),
        Owner_From: getVal(row, "OWNER_FROM", "Owner_From", "OwnerFrom", "owner_from"),
        Owner_Upto: getVal(row, "OWNER_UPTO", "Owner_Upto", "OwnerUpto", "owner_upto"),
        Owner_Name: getVal(row, "OWNER_NAME", "Owner_Name", "OwnerName", "owner_name"),
        Father_Husband_Name: getVal(row, "FATHER_HUSBAND_NAME", "Father_Husband_Name", "FatherHusbandName", "father_husband_name"),
        Present_Address: getVal(row, "PRESENT_ADDRESS", "Present_Address", "PresentAddress", "present_address"),
        Permanent_Address: getVal(row, "PERMANENT_ADDRESS", "Permanent_Address", "PermanentAddress", "permanent_address"),
        Owner_Type: getVal(row, "OWNER_TYPE", "Owner_Type", "OwnerType", "owner_type"),
        Sale_Auction_Date: getVal(row, "SALE_AUCTION_DATE", "Sale_Auction_Date", "SaleAuctionDate", "sale_auction_date"),
        Sale_Amount: getVal(row, "SALE_AMOUNT", "Sale_Amount", "SaleAmount", "sale_amount"),
        Reason: getVal(row, "REASON", "Reason", "reason"),
        Office: getVal(row, "OFFICE", "Office", "office"),
        Deemed_Transfer_Time_Days: getVal(row, "DEEMED_TRANSFER_TIME_DAYS", "Deemed_Transfer_Time_Days", "DeemedTransferTimeDays", "deemed_transfer_time_days"),
      }));
      break;
    case "historyOfChangedVehicle":
      mapped = rows.map((row) => ({
        Sr_No: getVal(row, "SR_NO", "Sr_No", "SrNo", "sr_no"),
        Changed_By: getVal(row, "CHANGED_BY", "Changed_By", "ChangedBy", "changed_by"),
        Changed_Data: getVal(row, "CHANGED_DATA", "Changed_Data", "ChangedData", "changed_data"),
        Changed_On: getVal(row, "CHANGED_ON", "Changed_On", "ChangedOn", "changed_on"),
        Office: getVal(row, "OFFICE", "Office", "office"),
      }));
      break;
    case "historyOfRcPrint":
      mapped = rows.map((row) => ({
        Application_No: getVal(row, "APPLICATION_NO", "Application_No", "ApplicationNo", "application_no"),
        Purpose: getVal(row, "PURPOSE", "Purpose", "purpose"),
        Printed_On: getVal(row, "PRINTED_ON", "Printed_On", "PrintedOn", "printed_on"),
        Printed_By: getVal(row, "PRINTED_BY", "Printed_By", "PrintedBy", "printed_by"),
      }));
      break;
    case "historyOfNoc":
      mapped = rows.map((row) => ({
        Application_No: getValOrEmpty(row, "APPLICATION_NO", "Application_No", "ApplicationNo", "application_no"),
        NOC_No: getValOrEmpty(row, "NOC_NO", "NOC_No", "NocNo", "noc_no"),
        NOC_Date: getValOrEmpty(row, "NOC_DATE", "NOC_Date", "NocDate", "noc_date"),
        State_To: getValOrEmpty(row, "STATE_TO", "State_To", "StateTo", "state_to"),
        Office_To: getValOrEmpty(row, "OFFICE_TO", "Office_To", "OfficeTo", "office_to"),
        Dispatch_No: getValOrEmpty(row, "DISPATCH_NO", "Dispatch_No", "DispatchNo", "dispatch_no"),
        New_Owner: getValOrEmpty(row, "NEW_OWNER", "New_Owner", "NewOwner", "new_owner"),
        Office: getValOrEmpty(row, "OFFICE", "Office", "office"),
      }));
      break;
    default:
      return rows;
  }

  // Ensure every backend column is present and merge any value from raw row so all details are saved
  const backendKeys = BACKEND_KEYS_BY_SECTION[saveKey];
  if (!backendKeys || !mapped) return mapped;
  return mapped.map((m, i) => {
    const out = { ...m };
    for (const k of backendKeys) {
      if (out[k] === undefined) out[k] = null;
    }
    mergeRowIntoMapped(rows[i], out, backendKeys, saveKey);
    return out;
  });
};

const TableParser = () => {
  const [inputText, setInputText] = useState("");
  const [parsedResult, setParsedResult] = useState(null);
  const [error, setError] = useState("");
  const [editingData, setEditingData] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [registrationNo, setRegistrationNo] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  // Table name (save key) to save data to - determines which database table receives the data
  const [selectedTableKey, setSelectedTableKey] = useState("");

  // Keep editingData in sync with parsed result; ensure every row has all headers (empty = "")
  useEffect(() => {
    if (parsedResult?.data?.length && parsedResult?.headers?.length) {
      const allKeys = parsedResult.headers;
      setEditingData(
        parsedResult.data.map((row) => {
          const full = {};
          allKeys.forEach((key) => {
            const v = row[key];
            full[key] = v !== undefined && v !== null ? String(v).trim() : "";
          });
          return full;
        })
      );
      const first = parsedResult.data[0];
      const regKey = Object.keys(first).find((k) => /registration_no|registrationno/i.test(k));
      if (regKey && first[regKey]) setRegistrationNo(String(first[regKey]).trim());
      const title = parsedResult?.tableTitle?.trim().toUpperCase?.() || "";
      const exactSaveKey = TABLE_TITLE_TO_SAVE_KEY[title];
      const inferredSaveKey = exactSaveKey || inferSaveKeyFromDetectedTitle(parsedResult?.tableTitle);
      if (inferredSaveKey) setSelectedTableKey(inferredSaveKey);
    } else {
      setEditingData([]);
    }
    setIsEditing(false);
  }, [parsedResult]);

  const handleParse = () => {
    setError("");
    setParsedResult(null);

    if (!inputText.trim()) {
      setError("Please paste table content");
      return;
    }

    try {
      const options = {
        headerLines: null,
        columnKeys: null,
        preserveEmptyColumns: true,
        rowStartRegex: /^\d+\b/,
      };

      const result = parseTableWithNormalizedHeaders(inputText, options);

      if (result.data.length === 0 && result.headers.length > 0) {
        setError("No data rows found. Check if your table format is correct.");
        return;
      }

      setParsedResult(result);
    } catch (err) {
      setError(`Parse error: ${err.message}`);
      console.error(err);
    }
  };

  const handleQuickParse = () => {
    // Quick parse is same as regular parse now
    handleParse();
  };

  const handleClear = () => {
    setInputText("");
    setParsedResult(null);
    setError("");
  };

  const copyJSON = () => {
    if (parsedResult && editingData.length) {
      const formatted = { ...parsedResult, data: editingData };
      const formattedJSON = formatTableResultAsJSON(formatted);
      navigator.clipboard.writeText(JSON.stringify(formattedJSON, null, 2));
      alert("JSON copied to clipboard!");
    }
  };

  const handleEditCell = (rowIdx, key, value) => {
    setEditingData((prev) => {
      const next = prev.map((row, i) => (i === rowIdx ? { ...row, [key]: value } : row));
      return next;
    });
  };

  const handleSaveToDb = async () => {
    if (!registrationNo.trim()) {
      alert("Registration Number is required to save.");
      return;
    }
    // Use selected table name from dropdown - data is saved to this database table
    const saveKey = selectedTableKey || TABLE_TITLE_TO_SAVE_KEY[parsedResult?.tableTitle?.trim().toUpperCase?.() || ""];
    if (!saveKey) {
      alert('Select a "Save to table" option so data is saved to the correct database table.');
      return;
    }
    const dbTableName = SAVE_KEY_TO_DB_TABLE[saveKey] || saveKey;
    setSaveLoading(true);
    try {
      const token = localStorage.getItem("token") || "";
      const mappedRows = mapParsedRowsToBackend(saveKey, editingData);
      // Use same API as automation: save-section → same backend path (saveChallanSection → saveChallanData with isAiConverted)
      const payload = {
        registrationNo: registrationNo.trim(),
        section: saveKey,
        data: mappedRows,
        // Backend should store dates in DB exactly as they are shown here.
        preserveInputDateFormat: true,
      };
      const res = await axios.post(`${API_BASE}/challan/save-section`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      alert(res?.data?.message || `Saved to ${dbTableName} successfully!`);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || err?.message || "Save failed.");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="table-parser-container">
      <div className="parser-header">
        <h1>Manual Parser</h1>
        <p>Paste your table content below. The parser will detect headers and preserve column alignment. Save to database sends all details to the same backend as automation.</p>
      </div>

      <div className="parser-controls">
        <div style={{ padding: "15px", background: "#e7f3ff", borderRadius: "6px", marginBottom: "15px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <span style={{ fontSize: "18px" }}>✨</span>
            <strong style={{ color: "#0066cc" }}>Auto-Detection Enabled</strong>
          </div>
          <div style={{ fontSize: "13px", color: "#555", lineHeight: "1.5" }}>
            The parser will automatically detect table titles and headers. Choose the target table below and click Save to store all details in the database.
          </div>
        </div>

        <div className="button-group">
          <button onClick={handleParse} className="parse-btn">
            Parse Table
          </button>
          <button onClick={handleClear} className="clear-btn">
            Clear
          </button>
        </div>
      </div>

      <div className="parser-content">
        <div className="input-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <label style={{ margin: 0 }}>
              <strong>Paste Table Content:</strong>
            </label>
            {inputText && (
              <div style={{ fontSize: "12px", color: "#666" }}>
                {inputText.split('\n').filter(l => l.trim()).length} lines
              </div>
            )}
          </div>
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Paste your table here (tab-separated or space-aligned)...&#10;&#10;Example (tab-separated):&#10;ID	REGISTRATION NO	SR NO	INSURANCE COMPANY&#10;129	AP31TH7433		THE NEW INDIA ASSURANCE CO LTD&#10;130	AP31TH7434	1	HDFC ERGO GENERAL INSURANCE"
            className="table-input"
            rows={15}
          />
          {inputText && inputText.includes('\t') && (
            <div style={{ marginTop: "10px", padding: "8px", background: "#e7f3ff", borderRadius: "4px", fontSize: "12px", color: "#0066cc" }}>
              ✓ Detected tab-separated table format
            </div>
          )}
        </div>

        <div className="output-section">
          {error && <div className="error-message">{error}</div>}

          {parsedResult && (
            <div className="result-container">
              <div className="result-header">
                <div>
                  <h3>Parsed Results</h3>
                  {parsedResult.tableTitle && (
                    <div className="table-title-badge">
                      📋 {parsedResult.tableTitle}
                    </div>
                  )}
                </div>
                <div className="result-stats">
                  <span>Headers: {parsedResult.headers.length}</span>
                  <span>Rows: {parsedResult.data.length}</span>
                  {parsedResult.detectedHeaderLines !== undefined && (
                    <span>Detected Header Lines: {parsedResult.detectedHeaderLines}</span>
                  )}
                </div>
                <button onClick={copyJSON} className="copy-btn">
                  Copy JSON
                </button>
              </div>

              {parsedResult.headers.length > 0 && (
                <div className="headers-display">
                  <h4>Detected Headers ({parsedResult.headers.length} columns):</h4>
                  {parsedResult.originalHeaders && (
                    <div style={{ marginBottom: "10px", fontSize: "12px", color: "#666", padding: "8px", background: "#f8f9fa", borderRadius: "4px" }}>
                      <strong>Original Headers:</strong> {parsedResult.originalHeaders.join(" → ")}
                    </div>
                  )}
                  <div className="headers-list">
                    {parsedResult.headers.map((header, idx) => (
                      <span key={idx} className="header-tag" title={header}>
                        {idx + 1}. {header || `(Empty ${idx + 1})`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="data-display">
                <div className="data-display-toolbar">
                  <h4 style={{ margin: 0 }}>Parsed Data Table ({editingData.length} rows):</h4>
                  <div className="data-display-buttons">
                    <button
                      type="button"
                      onClick={() => setIsEditing((e) => !e)}
                      className={isEditing ? "parse-btn secondary" : "parse-btn"}
                      style={{ fontSize: "12px", padding: "6px 12px" }}
                    >
                      {isEditing ? "Done editing" : "Edit"}
                    </button>
                    <button
                      onClick={() => {
                        const table = document.querySelector(".data-table table");
                        if (table) {
                          const range = document.createRange();
                          range.selectNode(table);
                          window.getSelection().removeAllRanges();
                          window.getSelection().addRange(range);
                          document.execCommand("copy");
                          window.getSelection().removeAllRanges();
                          alert("Table copied to clipboard!");
                        }
                      }}
                      className="copy-btn"
                      style={{ fontSize: "11px", padding: "4px 8px" }}
                    >
                      Copy Table
                    </button>
                  </div>
                </div>
                {editingData.length > 0 && parsedResult?.headers?.length > 0 ? (
                  <>
                    <p className="data-table-scroll-hint">Scroll horizontally → to view all columns</p>
                    <div className="data-table-wrapper">
                      <div className="data-table">
                        <table>
                          <thead>
                            <tr>
                              {parsedResult.headers.map((key, idx) => (
                                <th key={key} title={key}>
                                  {parsedResult.originalHeaders?.[idx] ?? key}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {editingData.map((row, idx) => (
                              <tr key={idx}>
                                {parsedResult.headers.map((key) => (
                                  <td key={key} title={row[key] != null && row[key] !== "" ? String(row[key]) : "(empty)"}>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        className="data-table-edit-input"
                                        value={row[key] ?? ""}
                                        onChange={(e) => handleEditCell(idx, key, e.target.value)}
                                      />
                                    ) : (
                                      row[key] !== "" && row[key] != null ? row[key] : <em style={{ color: "#999" }}>(empty)</em>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="no-data">No data rows found</p>
                )}
                {editingData.length > 0 && (
                  <div className="data-display-save-row">
                    <label className="data-display-save-label">
                      Save to table <span style={{ color: "#c00" }}>*</span> (database table name):
                    </label>
                    <select
                      className="control-input data-display-save-input"
                      value={selectedTableKey}
                      onChange={(e) => setSelectedTableKey(e.target.value)}
                      style={{ minWidth: "220px" }}
                    >
                      <option value="">— Select table —</option>
                      {SAVE_TABLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value} title={opt.tableName}>
                          {opt.label} → {opt.tableName}
                        </option>
                      ))}
                    </select>
                    {selectedTableKey && (
                      <span style={{ fontSize: "12px", color: "#0066cc", marginLeft: "8px" }}>
                        Target: {SAVE_KEY_TO_DB_TABLE[selectedTableKey]}
                      </span>
                    )}
                    <label className="data-display-save-label" style={{ marginLeft: "16px", marginTop: "8px" }}>
                      Registration Number <span style={{ color: "#c00" }}>*</span>:
                    </label>
                    <input
                      type="text"
                      className="control-input data-display-save-input"
                      value={registrationNo}
                      onChange={(e) => setRegistrationNo(e.target.value)}
                      placeholder="e.g. DL12CE9495"
                    />
                    <button
                      type="button"
                      onClick={handleSaveToDb}
                      disabled={saveLoading || !registrationNo.trim() || !selectedTableKey}
                      className="parse-btn secondary"
                      style={{ padding: "8px 18px" }}
                    >
                      {saveLoading ? "Saving..." : "Save to database"}
                    </button>
                  </div>
                )}
              </div>

              <div className="json-display">
                <h4>JSON Output:</h4>
                {parsedResult.tableTitle && (
                  <div style={{ marginBottom: "10px", fontSize: "12px", color: "#666", padding: "8px", background: "#e7f3ff", borderRadius: "4px" }}>
                    <strong>Table Name:</strong> <code>{titleToTableName(parsedResult.tableTitle)}</code>
                  </div>
                )}
                <pre className="json-output">
                  {JSON.stringify(formatTableResultAsJSON({ ...parsedResult, data: editingData }), null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableParser;
